import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send, Clock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { mockContactSubmission } from '../mock';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState({ question: '', answer: '', userAnswer: '' });
  const [captchaValid, setCaptchaValid] = useState(false);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const operations = ['+', '-', '*'];
    const operation = operations[Math.floor(Math.random() * operations.length)];
    
    let answer;
    let question;
    
    switch(operation) {
      case '+':
        answer = num1 + num2;
        question = `${num1} + ${num2}`;
        break;
      case '-':
        if (num1 >= num2) {
          answer = num1 - num2;
          question = `${num1} - ${num2}`;
        } else {
          answer = num2 - num1;
          question = `${num2} - ${num1}`;
        }
        break;
      case '*':
        answer = num1 * num2;
        question = `${num1} × ${num2}`;
        break;
      default:
        answer = num1 + num2;
        question = `${num1} + ${num2}`;
    }
    
    setCaptcha({ question, answer, userAnswer: '' });
    setCaptchaValid(false);
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const sanitizeInput = (input) => {
    return DOMPurify.sanitize(input.trim());
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const errors = [];
    
    if (!formData.name || formData.name.length < 2) {
      errors.push('Name must be at least 2 characters long');
    }
    
    if (!formData.email || !validateEmail(formData.email)) {
      errors.push('Please enter a valid email address');
    }
    
    if (!formData.message || formData.message.length < 10) {
      errors.push('Message must be at least 10 characters long');
    }
    
    if (!captchaValid) {
      errors.push('Please solve the captcha correctly');
    }
    
    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);
    
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
  };

  const handleCaptchaChange = (e) => {
    const userAnswer = e.target.value;
    setCaptcha(prev => ({ ...prev, userAnswer }));
    setCaptchaValid(parseInt(userAnswer) === captcha.answer);
  };

  const refreshCaptcha = () => {
    generateCaptcha();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      return;
    }
    
    setIsSubmitting(true);

    try {
      const sanitizedData = {
        name: sanitizeInput(formData.name),
        email: sanitizeInput(formData.email),
        company: sanitizeInput(formData.company),
        service: sanitizeInput(formData.service),
        message: sanitizeInput(formData.message)
      };
      
      const result = await mockContactSubmission(sanitizedData);
      if (result.success) {
        toast.success(result.message);
        setFormData({
          name: '',
          email: '',
          company: '',
          service: '',
          message: ''
        });
        generateCaptcha();
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section">
      <div className="dark-content-container">
        <div className="contact-header">
          <h2 className="display-large">Get in Touch</h2>
          <p className="body-large">
            Ready to enhance your security posture? Contact us for a consultation
          </p>
        </div>
        
        <div className="contact-grid">
          <div className="contact-info">
            <div className="contact-item">
              <Mail className="contact-icon" />
              <div>
                <h4 className="heading-3">Email</h4>
                <p className="body-medium">support@cyber-wiz.com</p>
              </div>
            </div>
            
            <div className="contact-item">
              <Phone className="contact-icon" />
              <div>
                <h4 className="heading-3">Phone</h4>
                <p className="body-medium">+1 (765) 360-9775</p>
              </div>
            </div>
            
            <div className="contact-item">
              <MapPin className="contact-icon" />
              <div>
                <h4 className="heading-3">Location</h4>
                <p className="body-medium">Nationwide Service Available</p>
              </div>
            </div>
            
            <div className="contact-item">
              <Clock className="contact-icon" />
              <div>
                <h4 className="heading-3">Response Time</h4>
                <p className="body-medium">Within 24 hours</p>
              </div>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name" className="form-label">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-input"
                  maxLength="100"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email" className="form-label">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input"
                  maxLength="255"
                  required
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="company" className="form-label">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="form-input"
                  maxLength="100"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="service" className="form-label">Service Interest</label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="">Select a service</option>
                  <option value="vulnerability-management">Vulnerability Management</option>
                  <option value="risk-assessment">Risk Assessment</option>
                  <option value="penetration-testing">Penetration Testing</option>
                  <option value="cmmc-audit">CMMC Readiness</option>
                  <option value="pci-dss">PCI DSS Audit</option>
                  <option value="third-party-audit">Third-Party Audit</option>
                </select>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="message" className="form-label">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                className="form-textarea"
                placeholder="Tell us about your security needs..."
                maxLength="1000"
                required
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Security Verification *</label>
              <div className="captcha-container">
                <div className="captcha-question">
                  <span className="captcha-text">What is {captcha.question}?</span>
                  <button 
                    type="button" 
                    onClick={refreshCaptcha}
                    className="captcha-refresh"
                    aria-label="Refresh captcha"
                  >
                    <RefreshCw size={16} />
                  </button>
                </div>
                <input
                  type="number"
                  value={captcha.userAnswer}
                  onChange={handleCaptchaChange}
                  className={`captcha-input ${captchaValid ? 'captcha-valid' : captcha.userAnswer ? 'captcha-invalid' : ''}`}
                  placeholder="Answer"
                  required
                />
                {captchaValid && <span className="captcha-check">✓</span>}
              </div>
            </div>
            
            <button 
              type="submit" 
              disabled={isSubmitting || !captchaValid}
              className="btn-primary form-submit"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
              <Send size={20} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;